'use client';

import dynamic from 'next/dynamic';
import { useCallback, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useLanguageRegionCode } from '@alltrails/core/hooks/useLanguageRegionCode';
import useWindowSize from '@alltrails/core/hooks/useWindowSize';
import { useExperiments, useExposureEvent } from '@alltrails/experiments';
import useSections from '../../hooks/useSections';
import { setSections } from '../../slices/sections';
import { useAppDispatch } from '../../utils/redux';
import type { HomepageSection, LocationData } from '../../utils/types';
import AppInstallSection from '../blocks/AppInstallSection';
import GearShop from '../blocks/GearShop';
import SeoLinks, { type SeoLinks as SeoLinksData } from '../blocks/SeoLinks';
import SponsoredLists from '../blocks/SponsoredLists';
import type { HitTrail } from '../blocks/TrailCarousel';
import TrailRiverTitle from '../TrailRiverTitle';

const ExploreAndDiscover = dynamic(() => import('../blocks/ExploreAndDiscover'));
const TrailCarousel = dynamic(() => import('../blocks/TrailCarousel'));
const UpgradeYourAdventures = dynamic(() => import('../blocks/UpgradeYourAdventures'));

type Props = {
  sections?: HomepageSection[];
  latLng?: [number, number];
  locationData?: LocationData;
};

const BlockSwitch = ({ sections: initialSections = [], latLng, locationData }: Props) => {
  const { width } = useWindowSize();
  const languageRegionCode = useLanguageRegionCode();
  const dispatch = useAppDispatch();
  const reduxSections = useSelector((state: { sections?: { sections?: HomepageSection[] } }) => state.sections?.sections);
  const sections = reduxSections || initialSections;
  const isUsEnglishLocale = languageRegionCode === 'en-US';
  const isEnglishLocale = isUsEnglishLocale || languageRegionCode === 'en-GB';

  const experiments = useExperiments();
  const appInstallExperimentVariant = experiments?.['web-growth-rm-homepage-app-install-module']?.value;

  const sendExposure = useExposureEvent('web-growth-rm-homepage-app-install-module');

  useEffect(() => {
    if (isEnglishLocale) {
      sendExposure();
    }
  }, [sendExposure, isEnglishLocale]);

  useEffect(() => {
    if (initialSections.length > 0 && !reduxSections) {
      dispatch(setSections(initialSections));
    }
  }, [initialSections, reduxSections, dispatch]);

  const processedSections = useSections({ initialSections: sections, latLng, locationData });

  const isAppInstallTreatmentActive = appInstallExperimentVariant === 'stacked' || appInstallExperimentVariant === 'condensed';
  const showAppInstallModule = isEnglishLocale && isAppInstallTreatmentActive;

  const excludedSections = [...(showAppInstallModule ? ['explore_and_discover', 'upgrade_your_adventures'] : [])];
  const filteredSections = processedSections.filter(section => !excludedSections.includes(section.section_type));

  const showGearShopSectionForUS = isUsEnglishLocale && locationData?.country === 'US';

  // Insert app install section as the second block
  // Translations will be added in this ticket: https://alltrails.atlassian.net/browse/GROW-15823
  const sectionsWithAppInstall: HomepageSection[] =
    showAppInstallModule && filteredSections.length > 0
      ? [filteredSections[0], { section_type: 'app_install_cta' }, ...filteredSections.slice(1)]
      : filteredSections;

  const renderSection = useCallback(
    (section: HomepageSection, idx: number) => {
      switch (section.section_type) {
        case 'seo_links': {
          const seoLinksData = (section.data as SeoLinksData) || [];
          if (seoLinksData.length === 0) return null;
          // When seo_homepage_curated_links experiment is enabled, 50 curated links are sent for en-US (includes POIs key); otherwise 15 links are sent
          const showAdditionalLinks = isUsEnglishLocale && seoLinksData.some(seoSection => seoSection.key === 'POIS');
          return <SeoLinks data={seoLinksData} showAdditionalLinks={showAdditionalLinks} />;
        }

        case 'sponsored_lists':
          return <SponsoredLists title={section.title} collections={section.collections} />;

        case 'merchandise_store':
          if (showGearShopSectionForUS) {
            return <GearShop />;
          }
          return null;

        case 'gear_shop_member_deals_tout':
          if (showGearShopSectionForUS) {
            return <GearShop tout="memberDeals" />;
          }
          return null;

        case 'trail_list': {
          return (
            <TrailCarousel
              hits={section.hits}
              title={<TrailRiverTitle section={section} idx={idx} locationData={locationData} languageRegionCode={languageRegionCode} />}
            />
          );
        }

        case 'googlebot_trail_list': {
          const hits = section.trails.concat({
            exploreMoreUrl: `/explore/${section.country_slug}`
          } as HitTrail);

          return <TrailCarousel hits={hits} title={section.title || ''} />;
        }

        case 'app_install_cta':
          return <AppInstallSection />;

        case 'explore_and_discover':
          return <ExploreAndDiscover />;

        case 'upgrade_your_adventures':
          return <UpgradeYourAdventures windowWidth={width} />;

        default:
          return null;
      }
    },
    [width, languageRegionCode, locationData, isUsEnglishLocale, showGearShopSectionForUS]
  );

  return (
    <>
      {sectionsWithAppInstall.map((section, index) => (
        <div key={`${section.section_type}-${index}`}>{renderSection(section, index)}</div>
      ))}
    </>
  );
};

export default BlockSwitch;
